import PlayerResource from './PlayerResource';
import type Player from '../Paella'

type CreateElementAttributes = {
    tag?: string
    attributes?: Record<string, string>
    children?: string | null
    parent?: HTMLElement | null
    innerText?: string | null
}

/**
 * Creates a DOM element with the specified configuration
 * @param {object} options - Element creation options
 * @param {string} [options.tag='div'] - HTML tag name
 * @param {Record<string, string>} [options.attributes={}] - Element attributes
 * @param {string} [options.children=""] - Inner HTML content
 * @param {string} [options.innerText=""] - Inner text content
 * @param {HTMLElement|null} [options.parent=null] - Parent element to append to
 * @returns {HTMLElement} The created DOM element
 */
export function createElement({ tag='div', attributes={}, children="", innerText="", parent=null } : CreateElementAttributes): HTMLElement {
    const result = document.createElement(tag);
    if (innerText !== null) {
        result.innerText = innerText;
    }

    for (let key in attributes) {
        result.setAttribute(key,attributes[key]);
    }
    if (children !== null) {
        result.innerHTML = children;
    }
    if (parent) {
        parent.appendChild(result);
    }
    return result;
}

const DANGEROUS_TAGS = new Set([
    'script', 'iframe', 'object', 'embed', 'base', 'meta', 'link',
    'form', 'input', 'textarea', 'select', 'math', 'template'
]);

const URL_ATTRS = new Set([
    'href', 'src', 'action', 'formaction', 'xlink:href', 'poster', 'background'
]);

function sanitizeElement(el: Element): void {
    for (const tag of DANGEROUS_TAGS) {
        Array.from(el.getElementsByTagName(tag)).forEach(e => e.remove());
    }

    const walker = (node: Element) => {
        Array.from(node.attributes).forEach(attr => {
            if (attr.name.startsWith('on')) {
                node.removeAttribute(attr.name);
            } else if (URL_ATTRS.has(attr.name) && /^\s*javascript\s*:/i.test(attr.value)) {
                node.removeAttribute(attr.name);
            }
        });
        Array.from(node.children).forEach(walker);
    };
    walker(el);
}

/**
 * Creates a DOM element from HTML text string
 * @param {string} htmlText - HTML string to parse
 * @param {HTMLElement|null} [parent=null] - Parent element to append to
 * @returns {HTMLElement} The created DOM element
 */
export function createElementWithHtmlText(htmlText: string, parent: HTMLElement | null = null): HTMLElement {
    const doc = new DOMParser().parseFromString(htmlText, 'text/html');
    sanitizeElement(doc.body);
    const result = doc.body.firstElementChild as HTMLElement;
    if (result) {
        document.adoptNode(result);
        if (parent) {
            parent.appendChild(result);
        }
    }
    return result;
}

type DomShowParams = {
    icon?: string | null
    text?: string
    timeout?: number
    position?: string
    cssClass?: string
}

/**
 * DomClass provides a base class for creating DOM-based components within the player.
 * It extends PlayerResource and manages a single DOM element with common operations.
 * @extends PlayerResource
 */
export class DomClass extends PlayerResource {
    #element: HTMLElement

    /**
     * Creates a new DomClass instance with a DOM element
     * @param {Paella} player - The player instance
     * @param {object} options - DOM element creation options
     * @param {string} [options.tag='div'] - HTML tag name for the element
     * @param {Record<string, string>} [options.attributes=[]] - Element attributes
     * @param {string} [options.children=""] - Inner HTML content
     * @param {HTMLElement|null} [options.parent=null] - Parent element to append to
     */
    constructor(player: Player, { tag='div', attributes={}, children="", parent=null } : CreateElementAttributes) {
        super(player);
        this.#element = createElement({ tag, attributes, children, parent });

        // Add a getter as a shortcut to the DOM element tag
        Object.defineProperty(this, tag, {
            get: () => this.#element
        });
    }

    /**
     * Gets the DOM element associated with this instance
     * @returns {HTMLElement} The DOM element
     */
    get element() {
        return this.#element;
    }

    /**
     * Gets the parent element of this DOM element
     * @returns {HTMLElement|null} The parent element, or null if no parent
     */
    get parent() {
        return this.#element.parentElement;
    }
    
    /**
     * Hides the DOM element by setting display style to "none"
     */
    hide() {
        this.element.style.display = "none";
    }
    
    /**
     * Shows the DOM element by removing the display style restriction
     * @param {string} [showMode="block"] - The display mode to use when showing
     */
    show(showMode: string | DomShowParams = "block") {
        this.element.style.display = "";
    }
    
    /**
     * Gets whether the element is currently visible
     * @returns {boolean} True if the element is visible, false otherwise
     */
    get isVisible() {
        const style = window.getComputedStyle(this.element);
        return  style.display !== "none" &&
                style.display !== "";
    }

    /**
     * Sets an attribute on the DOM element
     * @param {string} name - The attribute name
     * @param {string} value - The attribute value
     */
    setAttribute(name: string, value: string) {
        this.#element.setAttribute(name,value);
    }

    /**
     * Removes this element from its parent
     */
    removeFromParent() {
        this.#element.parentElement?.removeChild(this.#element);
    }
    
    /**
     * Sets a new parent for this element, removing it from the current parent first
     * @param {HTMLElement} parent - The new parent element
     */
    setParent(parent: HTMLElement) {
        this.removeFromParent();
        parent.appendChild(this.#element);
    }
}
